// Arcana photo mosaic + full-screen viewer, on top of Dawn's <media-gallery> (snippets/product-media-gallery.liquid).
// - Tapping any photo opens a full-screen viewer: swipe/arrows/← → keys, counter, filmstrip, zoom (click, tap,
//   Enter; drag to pan while zoomed) and an "All photos" grid. It replaces Dawn's product modal for images only;
//   videos and 3D models keep Dawn's. The phone back gesture closes the viewer.
// - Adds "+66 · All photos" to the last mosaic tile (kept on that tile when Dawn reorders photos for a variant),
//   an "All photos" pill on the phone photo and a progress line / button under the gallery.
// Photos are read from the gallery in its current order, so a variant's photo (moved first by Dawn) stays first.
// Dawn re-runs section scripts when a quick-add modal opens: the class is defined once, each gallery set up once.
if (!window.arcanaPhotos) {
  const GRID_ICON =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><g fill="currentColor"><rect x="1" y="1" width="6" height="6"/><rect x="9" y="1" width="6" height="6"/><rect x="1" y="9" width="6" height="6"/><rect x="9" y="9" width="6" height="6"/></g></svg>';
  const CLOSE_ICON =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
  const CARET_ICON =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 6l5 5 5-5" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const PLAY_ICON =
    '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3.5v9l7.5-4.5z" fill="currentColor"/></svg>';
  const ZOOM = 2.2;
  const phone = window.matchMedia('(max-width: 749px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  const sized = (src, width) => {
    try {
      const url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return src;
    }
  };

  // Strings come through Liquid's `t` filter, which HTML-escapes; decode before using them as text
  const decode = (value) => {
    const area = document.createElement('textarea');
    area.innerHTML = value || '';
    return area.value;
  };

  class ArcanaPhotos {
    constructor(gallery) {
      this.gallery = gallery;
      this.list = gallery.querySelector('.product__media-list');
      this.title = decode(gallery.dataset.arcanaTitle) || document.title;
      this.size = Number(gallery.dataset.arcanaMosaic) || 0;
      this.t = {};
      try {
        const raw = JSON.parse(gallery.querySelector('.arcana-photos-i18n').textContent);
        Object.keys(raw).forEach((key) => (this.t[key] = decode(raw[key])));
      } catch (e) {}
      this.index = 0;
      this.mode = 'photo';
      this.addChrome();
      gallery.addEventListener('click', (event) => this.onGalleryClick(event), true);
      this.list.addEventListener('scroll', () => this.queue(() => this.syncProgress()), { passive: true });
      // Dawn moves a variant's photo to the front: keep "+N" on the last mosaic tile
      new MutationObserver(() => this.placeMore()).observe(this.list, { childList: true });
      this.syncProgress();
    }

    queue(fn) {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        fn();
      });
    }

    // Every photo and video in the gallery's current order (videos/3D show their preview with a play badge)
    items() {
      return [...this.list.children].filter((li) => li.classList.contains('product__media-item') && li.querySelector('.product__media img'));
    }

    isImage(li) {
      return Boolean(li.querySelector('.product__modal-opener--image'));
    }

    text(key, values = {}) {
      return (this.t[key] || '').replace(/\[(\w+)\]/g, (m, name) => (name in values ? values[name] : m));
    }

    allButton(extraClass) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `arcana-photos-all ${extraClass}`;
      button.setAttribute('aria-haspopup', 'dialog');
      button.innerHTML = `${GRID_ICON}<span></span>`;
      button.querySelector('span').textContent = this.t.all_count || this.t.all || '';
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.open(this.currentInline(), 'grid', button);
      });
      return button;
    }

    addChrome() {
      const total = this.items().length;
      if (total < 2) return;

      const bar = document.createElement('div');
      bar.className = 'arcana-photos-bar quick-add-hidden';
      bar.innerHTML = `
        <span class="arcana-photos-bar__caption"></span>
        <span class="arcana-photos-bar__progress" aria-hidden="true"><span></span></span>`;
      bar.querySelector('.arcana-photos-bar__caption').textContent = this.t.hint || '';
      bar.append(this.allButton(''));
      this.gallery.querySelector('slider-component').after(bar);
      this.progress = bar.querySelector('.arcana-photos-bar__progress span');

      // Phones: "All photos" as a pill on the photo itself
      this.gallery.querySelector('slider-component').append(this.allButton('arcana-photos-pill quick-add-hidden'));

      // "+66 · All photos" on the last visible mosaic tile (tablet and up)
      const hidden = total - this.size;
      if (this.size > 1 && hidden > 0) {
        this.more = document.createElement('button');
        this.more.type = 'button';
        this.more.className = 'arcana-photos-more small-hide quick-add-hidden';
        this.more.setAttribute('aria-haspopup', 'dialog');
        this.more.innerHTML = '<b></b><span></span>';
        this.more.querySelector('b').textContent = this.text('more', { count: hidden });
        this.more.querySelector('span').textContent = this.t.all || '';
        this.more.setAttribute('aria-label', this.t.all_count || this.t.all || '');
        this.more.addEventListener('click', (event) => {
          event.stopPropagation();
          this.open(this.size - 1, 'grid', this.more);
        });
        this.placeMore();
      }
    }

    placeMore() {
      if (!this.more) return;
      const tile = this.list.children[this.size - 1];
      if (tile && this.more.parentElement !== tile) tile.append(this.more);
    }

    currentInline() {
      const slides = this.items();
      if (phone.matches) {
        const width = this.list.clientWidth || 1;
        return Math.min(slides.length - 1, Math.max(0, Math.round(this.list.scrollLeft / width)));
      }
      return Math.max(
        slides.findIndex((li) => li.classList.contains('is-active')),
        0
      );
    }

    syncProgress() {
      const total = this.items().length;
      if (!this.progress || !total) return;
      this.progress.style.width = `${((this.currentInline() + 1) / total) * 100}%`;
    }

    onGalleryClick(event) {
      const toggle = event.target.closest('.product__media-toggle');
      if (!toggle) return;
      const li = toggle.closest('.product__media-item');
      const index = this.items().indexOf(li);
      if (index < 0 || !this.isImage(li)) return; // video / 3D model: Dawn's modal
      event.preventDefault();
      event.stopPropagation();
      this.open(index, 'photo', toggle);
    }

    build() {
      const dialog = document.createElement('dialog');
      dialog.className = 'arcana-viewer';
      dialog.setAttribute('aria-label', this.title);
      dialog.innerHTML = `
        <div class="arcana-viewer__top">
          <p class="arcana-viewer__title"></p>
          <div class="arcana-viewer__controls">
            <span class="arcana-viewer__counter" aria-hidden="true"></span>
            <span class="visually-hidden" aria-live="polite" data-live></span>
            <div class="arcana-viewer__tabs" role="group">
              <button type="button" data-view="photo"></button>
              <button type="button" data-view="grid"></button>
            </div>
            <button type="button" class="arcana-viewer__icon" data-close>${CLOSE_ICON}</button>
          </div>
        </div>
        <div class="arcana-viewer__photo">
          <div class="arcana-viewer__strip"></div>
          <button type="button" class="arcana-viewer__icon arcana-viewer__prev" data-step="-1">${CARET_ICON}</button>
          <button type="button" class="arcana-viewer__icon arcana-viewer__next" data-step="1">${CARET_ICON}</button>
        </div>
        <div class="arcana-viewer__film"></div>
        <div class="arcana-viewer__grid"></div>`;
      dialog.querySelector('.arcana-viewer__title').textContent = this.title;
      dialog.querySelector('.arcana-viewer__tabs').setAttribute('aria-label', this.t.view || 'View');
      dialog.querySelector('[data-view="photo"]').textContent = this.t.photo || 'Photo';
      dialog.querySelector('[data-view="grid"]').innerHTML = `${GRID_ICON} `;
      dialog.querySelector('[data-view="grid"]').append(this.t.grid || 'All');
      dialog.querySelector('[data-close]').setAttribute('aria-label', this.t.close || 'Close');
      dialog.querySelector('[data-step="-1"]').setAttribute('aria-label', this.t.previous || 'Previous');
      dialog.querySelector('[data-step="1"]').setAttribute('aria-label', this.t.next || 'Next');
      document.body.append(dialog);

      this.dialog = dialog;
      this.strip = dialog.querySelector('.arcana-viewer__strip');
      this.film = dialog.querySelector('.arcana-viewer__film');
      this.grid = dialog.querySelector('.arcana-viewer__grid');
      this.counter = dialog.querySelector('.arcana-viewer__counter');
      this.live = dialog.querySelector('[data-live]');

      dialog.addEventListener('click', (event) => this.onViewerClick(event));
      dialog.addEventListener('keydown', (event) => this.onKey(event));
      dialog.addEventListener('close', () => this.onClose());
      dialog.addEventListener('pointermove', (event) => this.onPointerMove(event));
      dialog.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      dialog.addEventListener('pointerup', () => {
        this.lastDragMoved = Boolean(this.drag && this.drag.moved);
        this.drag = null;
      });
      dialog.addEventListener('pointercancel', () => (this.drag = null));
      this.strip.addEventListener('scroll', () => this.onStripScroll(), { passive: true });
      this.strip.addEventListener('scrollend', () => (this.programmatic = false));
      window.addEventListener('popstate', () => {
        if (this.dialog.open && !(history.state && history.state.arcanaViewer)) {
          this.closedByHistory = true;
          this.dialog.close();
        }
      });
    }

    fill() {
      // Rebuild every time, so a variant change (Dawn reorders the gallery) is picked up
      const photos = this.items().map((li) => {
        const img = li.querySelector('.product__media img');
        return { src: img.currentSrc || img.src, alt: img.alt, video: !this.isImage(li) };
      });
      this.dialog.toggleAttribute('data-single', photos.length < 2);
      this.photos = photos;
      const total = photos.length;
      const label = (i) => this.text('position', { index: i + 1, count: total });
      const zoomLabel = this.t.zoom || 'Zoom';
      this.strip.innerHTML = photos
        .map(
          (p, i) =>
            `<div class="arcana-viewer__slide${p.video ? ' arcana-viewer__slide--video' : ''}"><img src="${sized(p.src, 1600)}" srcset="${sized(p.src, 800)} 800w, ${sized(
              p.src,
              1200
            )} 1200w, ${sized(p.src, 1800)} 1800w" sizes="100vw" alt="" tabindex="0" role="button" loading="${
              Math.abs(i - this.index) < 2 ? 'eager' : 'lazy'
            }" decoding="async" draggable="false">${
              p.video ? `<button type="button" class="arcana-viewer__play" data-play="${i}">${PLAY_ICON}</button>` : ''
            }</div>`
        )
        .join('');
      const thumb = (p, i, number) =>
        `<button type="button" class="arcana-viewer__thumb" data-pick="${i}"><img src="${sized(
          p.src,
          240
        )}" alt="" loading="lazy">${number ? `<span aria-hidden="true">${i + 1}</span>` : ''}${
          p.video ? `<i class="arcana-viewer__badge" aria-hidden="true">${PLAY_ICON}</i>` : ''
        }</button>`;
      this.film.innerHTML = photos.map((p, i) => thumb(p, i, false)).join('');
      this.grid.innerHTML = photos.map((p, i) => thumb(p, i, true)).join('');
      this.strip.querySelectorAll('.arcana-viewer__slide > img').forEach((img, i) => {
        img.alt = photos[i].alt || label(i);
        if (photos[i].video) {
          img.removeAttribute('tabindex');
          img.removeAttribute('role');
        } else {
          img.setAttribute('aria-label', `${img.alt}. ${zoomLabel}`);
        }
      });
      this.strip.querySelectorAll('[data-play]').forEach((b) => b.setAttribute('aria-label', this.t.play || 'Play video'));
      this.dialog.querySelectorAll('[data-pick]').forEach((b) => b.setAttribute('aria-label', label(+b.dataset.pick)));
    }

    open(index, mode, opener) {
      if (!this.dialog) this.build();
      this.opener = opener || document.activeElement;
      this.index = index;
      this.fill();
      this.setMode(mode);
      this.dialog.showModal();
      document.body.classList.add('overflow-hidden');
      history.pushState({ arcanaViewer: true }, '');
      this.dialog.querySelector('[data-close]').focus({ preventScroll: true });
      requestAnimationFrame(() => {
        this.jump(this.index);
        this.setIndex(this.index, false);
        if (mode === 'grid') {
          const current = this.grid.querySelector(`[data-pick="${this.index}"]`);
          if (current) current.scrollIntoView({ block: 'center' });
        }
      });
    }

    onClose() {
      document.body.classList.remove('overflow-hidden');
      this.unzoom();
      if (!this.closedByHistory && history.state && history.state.arcanaViewer) history.back();
      this.closedByHistory = false;
      // Phones: leave the inline swipe photo on the one the shopper was looking at
      if (phone.matches) {
        const li = this.items()[this.index];
        if (li) this.list.scrollTo({ left: li.offsetLeft - this.list.offsetLeft, behavior: 'auto' });
      }
      if (this.handoff) {
        const handoff = this.handoff;
        this.handoff = null;
        this.openDawnModal(handoff);
        return;
      }
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }

    setMode(mode) {
      this.mode = mode;
      this.dialog.dataset.mode = mode;
      this.dialog.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === mode)));
      if (mode === 'photo') requestAnimationFrame(() => this.jump(this.index));
    }

    // Move the strip without its scroll events changing the index on the way
    jump(i, animate = false) {
      this.programmatic = true;
      clearTimeout(this.programmaticTimer);
      this.programmaticTimer = setTimeout(() => (this.programmatic = false), 700);
      this.strip.scrollTo({ left: i * this.strip.clientWidth, behavior: animate ? behavior() : 'auto' });
    }

    onStripScroll() {
      if (this.programmatic) return;
      this.queue(() => {
        const i = Math.round(this.strip.scrollLeft / Math.max(this.strip.clientWidth, 1));
        if (i !== this.index) this.setIndex(i, false);
      });
    }

    setIndex(i, scroll = true) {
      const total = this.photos.length;
      const next = (i + total) % total;
      const distance = Math.abs(next - this.index);
      this.index = next;
      this.unzoom();
      this.counter.textContent = `${this.index + 1} / ${total}`;
      this.live.textContent = this.text('position', { index: this.index + 1, count: total });
      this.dialog.querySelectorAll('[data-pick]').forEach((b) => b.setAttribute('aria-current', String(+b.dataset.pick === this.index)));
      const thumb = this.film.querySelector(`[data-pick="${this.index}"]`);
      if (thumb) this.film.scrollTo({ left: thumb.offsetLeft - this.film.clientWidth / 2 + thumb.offsetWidth / 2, behavior: behavior() });
      if (scroll) this.jump(this.index, distance === 1);
      [this.index - 1, this.index, this.index + 1].forEach((n) => {
        const img = this.strip.children[(n + total) % total]?.querySelector('img');
        if (img) img.loading = 'eager';
      });
    }

    zoomAt(slide, x, y) {
      const img = slide.querySelector('img');
      const zoomed = !slide.classList.contains('is-zoomed');
      this.unzoom();
      if (!zoomed) return;
      slide.classList.add('is-zoomed');
      this.strip.classList.add('is-zoomed');
      this.pan = { x: 0, y: 0 };
      this.zoomed = { slide, img };
      // Zoom toward the point that was tapped/clicked (x, y are 0–1 within the photo box)
      const w = img.offsetWidth;
      const h = img.offsetHeight;
      this.setPan((0.5 - x) * w * (ZOOM - 1), (0.5 - y) * h * (ZOOM - 1));
    }

    setPan(x, y) {
      const { img } = this.zoomed;
      const maxX = (img.offsetWidth * (ZOOM - 1)) / 2;
      const maxY = (img.offsetHeight * (ZOOM - 1)) / 2;
      this.pan = { x: Math.max(-maxX, Math.min(maxX, x)), y: Math.max(-maxY, Math.min(maxY, y)) };
      img.style.transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${ZOOM})`;
    }

    unzoom() {
      if (!this.strip) return;
      this.strip.classList.remove('is-zoomed');
      this.strip.querySelectorAll('.is-zoomed').forEach((slide) => {
        slide.classList.remove('is-zoomed');
        slide.querySelector('img').style.transform = '';
      });
      this.zoomed = null;
      this.drag = null;
    }

    onPointerDown(event) {
      if (!this.zoomed || !event.target.closest('.arcana-viewer__slide.is-zoomed')) return;
      this.drag = { x: event.clientX, y: event.clientY, panX: this.pan.x, panY: this.pan.y, moved: false };
    }

    onPointerMove(event) {
      if (!this.zoomed) return;
      if (this.drag) {
        const dx = event.clientX - this.drag.x;
        const dy = event.clientY - this.drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) this.drag.moved = true;
        this.setPan(this.drag.panX + dx, this.drag.panY + dy);
      } else if (event.pointerType === 'mouse' && event.target.closest('.arcana-viewer__slide.is-zoomed')) {
        // Mouse: the photo follows the cursor
        const r = this.zoomed.slide.getBoundingClientRect();
        const x = (event.clientX - r.left) / r.width;
        const y = (event.clientY - r.top) / r.height;
        const { img } = this.zoomed;
        this.setPan((0.5 - x) * img.offsetWidth * (ZOOM - 1), (0.5 - y) * img.offsetHeight * (ZOOM - 1));
      }
    }

    onViewerClick(event) {
      const t = event.target;
      if (t.closest('[data-close]')) return this.dialog.close();
      const view = t.closest('[data-view]');
      if (view) return this.setMode(view.dataset.view);
      const step = t.closest('[data-step]');
      if (step) return this.setIndex(this.index + Number(step.dataset.step));
      const pick = t.closest('[data-pick]');
      if (pick) {
        const i = Number(pick.dataset.pick);
        if (this.mode === 'grid') {
          this.setMode('photo');
          requestAnimationFrame(() => {
            this.setIndex(i, false);
            this.jump(i);
            // Keep keyboard focus in the viewer so ← → work straight away
            const img = this.strip.children[i]?.querySelector('img');
            (img && img.tabIndex === 0 ? img : this.dialog.querySelector('[data-close]')).focus({ preventScroll: true });
          });
          return;
        }
        return this.setIndex(i);
      }
      const play = t.closest('[data-play]');
      if (play) return this.play(Number(play.dataset.play));
      const videoSlide = t.closest('.arcana-viewer__slide--video');
      if (videoSlide) return this.play([...this.strip.children].indexOf(videoSlide));
      const slide = t.closest('.arcana-viewer__slide');
      if (slide && t.tagName === 'IMG' && !slide.classList.contains('arcana-viewer__slide--video')) {
        if (this.dragEnded()) return;
        const r = slide.getBoundingClientRect();
        this.zoomAt(slide, (event.clientX - r.left) / r.width, (event.clientY - r.top) / r.height);
      }
    }

    // Videos and 3D models play in Dawn's own media modal
    // The viewer finishes closing (scroll unlock, history) before Dawn's modal opens, so the modal keeps
    // its own scroll lock and focus; when it closes, focus returns to what first opened the viewer.
    play(i) {
      const li = this.items()[i];
      const opener = li && li.querySelector('modal-opener');
      const modal = opener && document.querySelector(opener.dataset.modal);
      const toggle = li && li.querySelector('.product__media-toggle');
      if (!modal || !toggle || typeof modal.show !== 'function') return;
      this.handoff = { modal, toggle };
      this.dialog.close();
    }

    openDawnModal({ modal, toggle }) {
      const returnTo = this.opener;
      modal.show(toggle);
      const watch = new MutationObserver(() => {
        if (modal.hasAttribute('open')) return;
        watch.disconnect();
        if (returnTo && returnTo.isConnected) requestAnimationFrame(() => returnTo.focus({ preventScroll: true }));
      });
      watch.observe(modal, { attributes: true, attributeFilter: ['open'] });
    }

    // A drag-to-pan ends with a click: don't treat it as "zoom out"
    dragEnded() {
      const moved = this.lastDragMoved;
      this.lastDragMoved = false;
      return moved;
    }

    onKey(event) {
      const slide = event.target.closest && event.target.closest('.arcana-viewer__slide:not(.arcana-viewer__slide--video)');
      if (slide && event.target.tagName === 'IMG' && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.zoomAt(slide, 0.5, 0.5);
        return;
      }
      if (this.mode !== 'photo') return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.setIndex(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }
  }

  const init = () =>
    document.querySelectorAll('media-gallery.arcana-mosaic').forEach((gallery) => {
      if (gallery.dataset.arcanaPhotos || gallery.closest('quick-add-modal')) return;
      gallery.dataset.arcanaPhotos = 'true';
      new ArcanaPhotos(gallery);
    });

  window.arcanaPhotos = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Theme editor reloads, and product swaps that replace the gallery (combined listings)
  document.addEventListener('shopify:section:load', init);
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
    subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(init));
  }
}
